import { useEffect, useState } from 'react';
import { Webhook as WebhookIcon, Plus, Loader2, Trash2, Send, ListChecks, RefreshCw, ChevronDown, Eye, EyeOff } from 'lucide-react';
import {
  getKatalog, listWebhooks, createWebhook, patchWebhook, deleteWebhook, testWebhook,
  listDeliveries, retryDelivery, listRegeln, createRegel, deleteRegel,
  type WebhookDto, type DeliveryDto, type RegelDto, type KatalogDto, type Bedingung,
} from '../lib/automationApi';
import ConfirmDialog from './admin/ConfirmDialog';

interface Props { darkMode: boolean; cardClasses: string; inputClasses: string; }

const zeit = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const OBJEKT_TEXTE: Record<string, string> = { customer: 'Kunde', vorgang: 'Vorgang', report: 'Bericht' };

/**
 * Webhooks und Regeln auf EINER Seite — sie gehören zusammen: eine Regel ohne Ziel
 * meldet nirgendwohin, ein Ziel ohne Regel schweigt.
 */
export default function AutomationSettings({ darkMode, cardClasses, inputClasses }: Props) {
  const [katalog, setKatalog] = useState<KatalogDto | null>(null);
  const [hooks, setHooks] = useState<WebhookDto[]>([]);
  const [regeln, setRegeln] = useState<RegelDto[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');

  // Anlegen eines Webhooks
  const [url, setUrl] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [legtAn, setLegtAn] = useState(false);

  const [offenesProtokoll, setOffenesProtokoll] = useState<number | null>(null);
  const [deliveries, setDeliveries] = useState<DeliveryDto[]>([]);
  const [geheimnisSichtbar, setGeheimnisSichtbar] = useState<number | null>(null);
  const [loeschHook, setLoeschHook] = useState<WebhookDto | null>(null);

  // Anlegen einer Regel
  const [rName, setRName] = useState('');
  const [rSubject, setRSubject] = useState<'customer' | 'vorgang' | 'report'>('vorgang');
  const [rFeld, setRFeld] = useState('');
  const [rOp, setROp] = useState('gte');
  const [rWert, setRWert] = useState('');
  const [rHook, setRHook] = useState<number | ''>('');

  const laden = () => {
    setLaedt(true);
    Promise.all([listWebhooks(), listRegeln(), getKatalog()])
      .then(([h, r, k]) => { setHooks(h); setRegeln(r); setKatalog(k); })
      .catch((e: any) => setFehler(e?.message || 'Konnte nicht geladen werden'))
      .finally(() => setLaedt(false));
  };
  useEffect(laden, []);

  useEffect(() => {
    const felder = katalog?.felder?.[rSubject];
    if (felder?.length && !felder.some(f => f.feld === rFeld)) setRFeld(felder[0].feld);
  }, [katalog, rSubject, rFeld]);

  const hookAnlegen = async () => {
    if (!url.trim() || gewaehlt.length === 0) return;
    setLegtAn(true); setFehler(''); setHinweis('');
    try {
      await createWebhook(url.trim(), gewaehlt);
      setUrl(''); setGewaehlt([]); laden();
    } catch (e: any) { setFehler(e?.message || 'Anlegen fehlgeschlagen'); }
    finally { setLegtAn(false); }
  };

  const protokollOeffnen = async (id: number) => {
    if (offenesProtokoll === id) { setOffenesProtokoll(null); return; }
    setOffenesProtokoll(id);
    try { setDeliveries(await listDeliveries(id)); } catch { setDeliveries([]); }
  };

  const regelAnlegen = async () => {
    if (!rName.trim() || !rFeld || rHook === '') return;
    const bedingung: Bedingung = { feld: rFeld, op: rOp, ...(['exists', 'empty'].includes(rOp) ? {} : { wert: rWert }) };
    setFehler('');
    try {
      await createRegel({ name: rName.trim(), subject: rSubject, conditions: [bedingung], webhookId: Number(rHook) });
      setRName(''); setRWert(''); laden();
    } catch (e: any) { setFehler(e?.message || 'Regel konnte nicht angelegt werden'); }
  };

  const leise = darkMode ? 'text-slate-400' : 'text-slate-500';
  const feldTexte = katalog?.felder?.[rSubject] ?? [];
  const opText = (op: string) => katalog?.operatoren.find(o => o.name === op)?.text ?? op;
  const feldText = (subject: string, feld: string) =>
    katalog?.felder?.[subject]?.find(f => f.feld === feld)?.text ?? feld;

  if (laedt) {
    return <div className={`${cardClasses} flex items-center gap-2 text-xs ${leise}`}><Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…</div>;
  }

  return (
    <div className="space-y-6">
      {/* ── Webhooks ─────────────────────────────────────────────── */}
      <div className={cardClasses}>
        <div className="flex items-start gap-3 mb-4">
          <WebhookIcon className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className="min-w-0">
            <h3 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Webhooks</h3>
            <p className={`text-xs mt-1 ${leise}`}>
              Statt im Minutentakt zu fragen, ob sich etwas getan hat: Wir melden es. Jede Zustellung
              trägt eine Signatur (<code>X-Startklar-Signature</code>) über Zeitstempel und Rumpf —
              prüfe sie beim Empfänger, sonst kann jeder, der die Adresse kennt, falsche Ereignisse
              einspeisen. Fehlversuche werden mit wachsendem Abstand wiederholt.
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-3">
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://dein-dienst.de/webhook"
            className={`${inputClasses} w-full`}
          />
          <div className="flex flex-wrap gap-1.5">
            {katalog?.events.map(e => {
              const an = gewaehlt.includes(e.name);
              return (
                <button
                  key={e.name}
                  type="button"
                  title={e.text}
                  onClick={() => setGewaehlt(g => an ? g.filter(x => x !== e.name) : [...g, e.name])}
                  className={`px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                    an
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >{e.name}</button>
              );
            })}
          </div>
          <button
            onClick={hookAnlegen}
            disabled={legtAn || !url.trim() || gewaehlt.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {legtAn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Webhook anlegen
          </button>
        </div>

        {fehler && <p className="text-xs text-red-600 mb-2">{fehler}</p>}
        {hinweis && <p className={`text-xs mb-2 ${leise}`}>{hinweis}</p>}

        {hooks.length === 0 ? (
          <p className={`text-xs ${leise}`}>Noch kein Webhook eingerichtet.</p>
        ) : (
          <ul className="space-y-2">
            {hooks.map(h => (
              <li key={h.id} className={`rounded-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5">
                  <code className={`text-xs ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{h.url}</code>
                  {!h.active && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                      stillgelegt nach {h.failCount} Fehlschlägen
                    </span>
                  )}
                  <span className={`text-xs ${leise}`}>{h.events.join(', ')}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => setGeheimnisSichtbar(g => g === h.id ? null : h.id)} title="Signatur-Geheimnis anzeigen"
                      className={`p-1.5 rounded ${leise} hover:bg-slate-100 dark:hover:bg-slate-700`}>
                      {geheimnisSichtbar === h.id ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={async () => { await testWebhook(h.id); setHinweis('Probezustellung eingereiht — siehe Protokoll.'); protokollOeffnen(h.id); }}
                      title="Probezustellung mit erfundenen Daten"
                      className={`p-1.5 rounded ${leise} hover:bg-slate-100 dark:hover:bg-slate-700`}><Send className="w-3.5 h-3.5" /></button>
                    <button onClick={() => protokollOeffnen(h.id)} title="Zustellprotokoll"
                      className={`p-1.5 rounded ${leise} hover:bg-slate-100 dark:hover:bg-slate-700`}><ListChecks className="w-3.5 h-3.5" /></button>
                    {!h.active && (
                      <button onClick={async () => { await patchWebhook(h.id, { active: true }); laden(); }} title="Wieder einschalten"
                        className="p-1.5 rounded text-green-600 hover:bg-green-50 dark:hover:bg-slate-700"><RefreshCw className="w-3.5 h-3.5" /></button>
                    )}
                    <button onClick={() => setLoeschHook(h)} className="p-1.5 rounded text-red-600 hover:bg-red-50 dark:hover:bg-slate-700"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>

                {geheimnisSichtbar === h.id && (
                  <div className={`px-3 pb-2.5 text-xs ${leise}`}>
                    <code className={`px-2 py-1 rounded ${darkMode ? 'bg-slate-900 text-slate-200' : 'bg-slate-100 text-slate-800'}`}>{h.secret}</code>
                    <span className="ml-2">Damit prüfst du die Signatur: HMAC-SHA256 über „Zeitstempel.Rumpf".</span>
                  </div>
                )}

                {offenesProtokoll === h.id && (
                  <div className={`border-t px-3 py-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    {deliveries.length === 0 ? (
                      <p className={`text-xs ${leise}`}>Noch keine Zustellung.</p>
                    ) : (
                      <ul className="space-y-1">
                        {deliveries.map(d => (
                          <li key={d.id} className="flex flex-wrap items-center gap-2 text-xs">
                            <span className={`px-1.5 py-0.5 rounded-full font-medium ${
                              d.status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                              : d.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                            }`}>{d.status}</span>
                            <span className={darkMode ? 'text-slate-300' : 'text-slate-700'}>{d.event}</span>
                            <span className={leise}>Versuch {d.attempt}</span>
                            <span className={leise}>{d.responseStatus ?? '—'}</span>
                            <span className={`${leise} truncate max-w-[16rem]`} title={d.responseBody ?? ''}>{d.responseBody ?? ''}</span>
                            <span className={leise}>{zeit(d.deliveredAt ?? d.createdAt)}</span>
                            {d.verlauf?.length > 1 && (
                              <span className={leise} title={d.verlauf.map(v => `Versuch ${v.versuch}: ${v.responseStatus ?? 'kein Kontakt'} — ${v.antwort ?? ''}`).join('\n')}>
                                {d.verlauf.length} Versuche
                              </span>
                            )}
                            {/* Auch erfolgreiche Zustellungen sind erneut sendbar — z. B. wenn
                                der Empfänger sie zwar annahm, aber verworfen hat. */}
                            <button onClick={async () => { await retryDelivery(d.id); setDeliveries(await listDeliveries(h.id)); }}
                              className="ml-auto text-blue-600 hover:underline">erneut senden</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Regeln ───────────────────────────────────────────────── */}
      <div className={cardClasses}>
        <div className="flex items-start gap-3 mb-4">
          <ChevronDown className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className="min-w-0">
            <h3 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Regeln</h3>
            <p className={`text-xs mt-1 ${leise}`}>
              „Wenn das zutrifft, melde es dorthin." Eine Regel meldet <strong>an der Kante</strong>:
              einmal, wenn die Bedingung erstmals zutrifft — und erst wieder, nachdem sie
              zwischendurch nicht mehr galt. Sonst meldete „über 80 %" nach jeder Neuberechnung erneut.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-3">
          <input value={rName} onChange={e => setRName(e.target.value)} placeholder="Name der Regel" className={`${inputClasses} flex-1 min-w-[10rem]`} />
          <select value={rSubject} onChange={e => setRSubject(e.target.value as any)} className={inputClasses}>
            {Object.entries(OBJEKT_TEXTE).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
          <select value={rFeld} onChange={e => setRFeld(e.target.value)} className={inputClasses}>
            {feldTexte.map(f => <option key={f.feld} value={f.feld}>{f.text}</option>)}
          </select>
          <select value={rOp} onChange={e => setROp(e.target.value)} className={inputClasses}>
            {katalog?.operatoren.map(o => <option key={o.name} value={o.name}>{o.text}</option>)}
          </select>
          {!['exists', 'empty'].includes(rOp) && (
            <input value={rWert} onChange={e => setRWert(e.target.value)} placeholder="Wert" className={`${inputClasses} w-28`} />
          )}
          <select value={rHook} onChange={e => setRHook(e.target.value === '' ? '' : Number(e.target.value))} className={inputClasses}>
            <option value="">Ziel wählen…</option>
            {hooks.map(h => <option key={h.id} value={h.id}>{h.url}</option>)}
          </select>
          <button onClick={regelAnlegen} disabled={!rName.trim() || rHook === ''}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> Regel anlegen
          </button>
        </div>

        {regeln.length === 0 ? (
          <p className={`text-xs ${leise}`}>Noch keine Regel. Ohne Regel schweigt ein Webhook, sofern er kein Ereignis abonniert hat.</p>
        ) : (
          <ul className="space-y-2">
            {regeln.map(r => (
              <li key={r.id} className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 rounded-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <span className={`text-xs font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{r.name}</span>
                <span className={`text-xs ${leise}`}>
                  {OBJEKT_TEXTE[r.subject]}: {r.conditions.map(c => `${feldText(r.subject, c.feld)} ${opText(c.op)} ${c.wert ?? ''}`).join(' und ')}
                </span>
                <span className={`text-xs ${leise}`}>→ {hooks.find(h => h.id === r.webhookId)?.url ?? 'Ziel gelöscht'}</span>
                <button onClick={async () => { await deleteRegel(r.id); laden(); }}
                  className="ml-auto p-1.5 rounded text-red-600 hover:bg-red-50 dark:hover:bg-slate-700"><Trash2 className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!loeschHook}
        title="Webhook löschen"
        message={`„${loeschHook?.url}" wird gelöscht — samt Zustellprotokoll und allen Regeln, die dorthin melden.`}
        confirmLabel="Löschen"
        onConfirm={async () => { const h = loeschHook; setLoeschHook(null); if (h) { await deleteWebhook(h.id); laden(); } }}
        onCancel={() => setLoeschHook(null)}
      />
    </div>
  );
}
